import { QueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { useApiSuspenseInfiniteQuery } from '../../../hooks/useApiSuspenseInfiniteQuery';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';
import { LogbooksPage } from '../types/listTypes';
import { fetchLogbookList } from '../api/listApi';
import { useParams } from 'react-router-dom';

const getNextPageParam = (lastPage: LogbooksPage, allPages: LogbooksPage[]): number | undefined => {
    if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
    }
    return undefined;
};

const logBookQuery = (searchString: string, plantId: string, limit: number) => ({
    queryKey: ['configuredLogbooks', searchString, plantId],
    queryFn: (props: any) => fetchLogbookList({...props, plantId, limit}),
    getNextPageParam,
    initialPageParam: 1,
});

export const useLogbookTable = (searchString: string, limit = 20) => {
    const lastElementRef = useRef<HTMLElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const { plantId } = useParams();
    const queryKey = ['configuredLogbooks', searchString, plantId];
    const { data, ...queryResult } = useApiSuspenseInfiniteQuery(logBookQuery(searchString, plantId, limit));
    
    const { allLogbooks, totalCount } = useMemo(() => {
        const all = data?.pages.flatMap(page => page.logbooks) ?? [];
        const total = data?.pages[0]?.totalCount ?? 0;
        return { allLogbooks: all, totalCount: total };
    }, [data]);

    useIntersectionObserver(
        tableContainerRef,
        lastElementRef,
        queryResult.fetchNextPage,
        queryResult.hasNextPage,
        queryResult.isFetchingNextPage
    );

    return {
        logbooks: allLogbooks,
        totalCount,
        lastElementRef,
        tableContainerRef,
        queryResult,
        queryKey,
    };
};

export const logbookListLoader = (queryClient: QueryClient) => async ({ params, request }: { params: Params<string>; request: Request }) => {
    const url = new URL(request.url);
    const searchString = url.searchParams.get('search') || '';
    const limit = parseInt(url.searchParams.get('limit') || '20');
    const assetId = params.plantId || '';

    const query = logBookQuery(searchString, assetId, limit);
    
    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchInfiniteQuery(query))
    );
  };